import { useCallback, useEffect, useMemo, useRef } from "react";

// ─────────────────────────────────────────────────────────────────────────────
// Croquis del recinto — plano esquematico con la gente en movimiento.
//
// Por que existe: las vistas de flujos y contingencias pintaban manchas de
// color difuminadas sobre un rectangulo oscuro vacio. Quien conoce el proyecto
// entiende que la mancha ambar es "la plaza se esta llenando"; el stakeholder
// ve una mancha. Aqui la gente son puntos sobre el plano del recinto: se ve
// acumularse y frenarse, sin que nadie tenga que explicarlo. Eso importa
// porque el 7-ago presenta el cliente solo, y nosotros no estamos en la sala.
//
// La regla que gobierna este archivo (alcance_v2.md §7): esto es un ESQUEMA,
// no una camara. Un plano nadie lo confunde con video en vivo, y por eso se
// eligio en vez de dibujar recuadros de deteccion inventados sobre el video
// real del motor — que es la trampa que se quito del repo el 3-ago.
//
// El llenado de cada zona NO se declara aqui: sale de la MISMA fila que
// muestra la tabla de la derecha (`filas[].barra` y `filas[].tono`). Si el
// plano y los numeros se contradijeran, el cliente lo veria en la misma
// pantalla, asi que se leen de una sola fuente.
// ─────────────────────────────────────────────────────────────────────────────

// Espacio de coordenadas del plano: 16:9, para que calce con el contenedor
// aspect-video de <Escena>. Las barras de arriba y abajo de <Escena> tapan unas
// 10 unidades por lado, asi que el contenido vive entre y=14 y y=80.
const W = 160;
const H = 90;

// Cuanto tarda una persona en recorrer una ruta completa a velocidad 1.
const SEG_POR_RUTA = 16;

// Las zonas se llaman IGUAL que las filas de la tabla (EVENTOS[*].pasos[*].filas):
// ese nombre es la llave que une el dibujo con los numeros.
const ZONAS = [
  { id: "Acceso norte", testid: "acceso-norte", x: 10, y: 16, w: 36, h: 22, cupo: 16 },
  { id: "Plaza central", testid: "plaza-central", x: 54, y: 22, w: 54, h: 44, cupo: 40 },
  { id: "Salida sur", testid: "salida-sur", x: 112, y: 52, w: 36, h: 26, cupo: 18 },
];

// Decorado: no lleva gente ni estado, solo ubica al que mira el plano.
const ESCENARIO = { x: 112, y: 14, w: 36, h: 16 };

// Mitades del recinto. Existen porque las filas del evento de incendio hablan
// de "Zona oriente" y "Zona poniente", y el plano tiene que usar esos nombres.
const MITADES = [
  { id: "poniente", etiqueta: "PONIENTE", x: 0, w: 80 },
  { id: "oriente", etiqueta: "ORIENTE", x: 80, w: 80 },
];

// Recorridos de la gente. El orden importa: la primera es la que alimenta la
// plaza y la segunda la que desagua hacia la salida sur.
const RUTAS = {
  "norte-plaza": [[28, 27], [48, 32], [66, 40], [81, 44]],
  "plaza-sur": [[81, 44], [100, 50], [118, 58], [130, 65]],
  "plaza-interna": [[60, 28], [102, 30], [102, 60], [60, 58], [60, 28]],
};

// Mismos colores que la tabla de la derecha (TONOS en DemoDashboard.jsx).
const TONO_RGB = {
  good: "16,185,129",
  warn: "245,158,11",
  bad: "239,68,68",
  "": "37,99,235",
};

// Puntos de paso: donde el recinto se angosta. Un cuello de botella no ocurre
// "en una zona", ocurre en el PASO entre dos, y por eso se marca como un punto y
// no como un rectangulo. Tres estados, y el que importa para el demo es el de en
// medio: el sistema puede señalar donde TODAVIA NO pasa pero va a pasar, porque
// lo deduce de hacia donde va la gente.
const ESTILO_CUELLO = {
  fluye: { rgb: "56,189,248", grosor: 0.55, dash: "", etiqueta: "FLUYE", alfa: 0.5 },
  riesgo: { rgb: "245,158,11", grosor: 0.8, dash: "1.6 1.4", etiqueta: "POSIBLE CUELLO", alfa: 1 },
  cuello: { rgb: "239,68,68", grosor: 1, dash: "", etiqueta: "CUELLO DE BOTELLA", alfa: 1 },
};

const VACIO = {};

// Pseudoaleatorio determinista: la misma semilla da siempre el mismo punto. Con
// Math.random() el plano parpadearia en cada re-render y se veria distinto en
// cada corrida del demo.
function azar(semilla) {
  const x = Math.sin(semilla * 12.9898) * 43758.5453;
  return x - Math.floor(x);
}

function medirRuta(puntos) {
  const segmentos = [];
  let total = 0;
  for (let i = 1; i < puntos.length; i += 1) {
    const a = puntos[i - 1];
    const b = puntos[i];
    const d = Math.hypot(b[0] - a[0], b[1] - a[1]);
    segmentos.push({ a, b, d });
    total += d;
  }
  return { segmentos, total };
}

const RUTAS_MEDIDAS = Object.keys(RUTAS).reduce((acc, k) => {
  acc[k] = medirRuta(RUTAS[k]);
  return acc;
}, {});

// Posicion sobre una ruta, con t normalizado (0 = inicio, 1 = fin, y da la vuelta).
function puntoEnRuta(ruta, t) {
  const { segmentos, total } = RUTAS_MEDIDAS[ruta];
  let d = (((t % 1) + 1) % 1) * total;
  for (let i = 0; i < segmentos.length; i += 1) {
    const s = segmentos[i];
    if (d <= s.d || i === segmentos.length - 1) {
      const f = s.d === 0 ? 0 : Math.min(d / s.d, 1);
      return [s.a[0] + (s.b[0] - s.a[0]) * f, s.a[1] + (s.b[1] - s.a[1]) * f];
    }
    d -= s.d;
  }
  return segmentos[segmentos.length - 1].b;
}

// Ocupacion y color de una zona. Primero la fila de la tabla: es la fuente de
// verdad. `croquis.zonas` solo cubre los pasos cuya fila es cualitativa
// ("empezando a saturar") y por eso no trae barra.
function estadoDeZona(zona, filas, cfg) {
  const fila = filas.find((f) => f.k === zona.id);
  const deCroquis = cfg.zonas ? cfg.zonas[zona.id] : undefined;
  const ocupacion =
    fila && typeof fila.barra === "number" ? fila.barra / 100 : deCroquis != null ? deCroquis : 0.25;
  const tono = (fila && fila.tono) || (cfg.tonos && cfg.tonos[zona.id]) || "";
  return { ...zona, ocupacion, tono };
}

// La gente. Dos clases: la que esta dentro de una zona (define el llenado, se
// mueve poco) y la que va en transito por las rutas (define la direccion y la
// velocidad). Juntas cuentan la historia: cuanta gente hay y hacia donde va.
function construirGente(zonas, cfg) {
  const gente = [];

  zonas.forEach((z, iz) => {
    const cuantos = Math.round(z.ocupacion * z.cupo);
    // La semilla arranca en el indice de la zona: al llenarse, los puntos que
    // ya estaban se quedan en su lugar y solo se suman los nuevos. Se lee como
    // "llego mas gente", no como que la escena se reacomodo.
    for (let k = 0; k < cuantos; k += 1) {
      const s = iz * 977 + k * 7 + 1;
      gente.push({
        tipo: "zona",
        bx: z.x + 3 + azar(s) * (z.w - 6),
        by: z.y + 3 + azar(s + 101) * (z.h - 6),
        fase: azar(s + 211) * 6.283,
      });
    }
  });

  // Un grupo que se junta alrededor de un punto: los curiosos de la riña.
  if (cfg.cumulo) {
    const { x, y, r, n } = cfg.cumulo;
    for (let k = 0; k < n; k += 1) {
      const ang = azar(k * 13 + 3) * 6.283;
      const dist = r * (0.35 + azar(k * 29 + 5) * 0.65);
      gente.push({
        tipo: "zona",
        bx: x + Math.cos(ang) * dist,
        by: y + Math.sin(ang) * dist * 0.6,
        fase: azar(k * 37 + 7) * 6.283,
      });
    }
  }

  // En transito. Con `converge` todos toman la misma ruta: es lo que hace
  // visible el "direcciones: una sola" de la tabla.
  const rutas = cfg.converge ? ["plaza-sur"] : Object.keys(RUTAS);
  const cuantos = cfg.transito != null ? cfg.transito : 22;
  for (let k = 0; k < cuantos; k += 1) {
    gente.push({
      tipo: "ruta",
      ruta: rutas[k % rutas.length],
      t0: azar(k * 19 + 11),
      vel: 0.85 + azar(k * 23 + 13) * 0.4,
    });
  }

  return gente;
}

function posicionInicial(p) {
  return p.tipo === "ruta" ? puntoEnRuta(p.ruta, p.t0) : [p.bx, p.by];
}

// Direccion del recorrido en un punto: sirve para orientar las flechas de flujo.
function anguloEnRuta(ruta, t) {
  const [x1, y1] = puntoEnRuta(ruta, t - 0.01);
  const [x2, y2] = puntoEnRuta(ruta, t + 0.01);
  return (Math.atan2(y2 - y1, x2 - x1) * 180) / Math.PI;
}

export default function CroquisRecinto({ filas = [], croquis }) {
  const cfg = croquis || VACIO;

  const zonas = useMemo(() => ZONAS.map((z) => estadoDeZona(z, filas, cfg)), [filas, cfg]);
  const gente = useMemo(() => construirGente(zonas, cfg), [zonas, cfg]);

  const nodos = useRef([]);
  const focoRef = useRef(null);
  const anillos = useRef([]);
  const pulsos = useRef([]);

  const velocidad = cfg.velocidad != null ? cfg.velocidad : 1;
  const foco = cfg.foco;

  // "aforo" = vista de flujos: las zonas se pintan por cuánta gente hay.
  // "termico" = incendio: el plano se lee por calor, no por aforo.
  // "alerta" = riña: zonas neutras, el color lo lleva lo que se está marcando.
  const modo = cfg.modo || "aforo";
  const modoAforo = modo === "aforo";
  const termico = modo === "termico";

  // Lo que se dibuja en el aviso de riña sale de las MISMAS filas de la tabla:
  // la distancia entre las dos personas y cuánta gente se congregó. Es la regla
  // del croquis — un solo lugar donde se declara cada cosa.
  const rina = useMemo(() => {
    if (!cfg.destacados || cfg.destacados.length !== 2) return null;
    const [a, b] = cfg.destacados;
    const distancia = filas.find((f) => f.k === "Distancia entre ambas");
    const cerca = filas.find((f) => f.k === "Otras personas cerca");
    return {
      mx: (a.x + b.x) / 2,
      my: (a.y + b.y) / 2,
      distancia: distancia ? distancia.v : null,
      // El corro solo se rotula cuando de verdad hay gente juntándose.
      cerca: cfg.cumulo && cerca ? cerca.v : null,
    };
  }, [cfg, filas]);

  // Respetar a quien pidio menos movimiento: se pinta un solo cuadro fijo.
  const reducido = useMemo(
    () =>
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    []
  );

  const pintar = useCallback(
    (t) => {
      for (let i = 0; i < gente.length; i += 1) {
        const el = nodos.current[i];
        if (!el) continue;
        const p = gente[i];
        let x;
        let y;
        if (p.tipo === "ruta") {
          [x, y] = puntoEnRuta(p.ruta, p.t0 + (t / SEG_POR_RUTA) * p.vel * velocidad);
        } else {
          x = p.bx + Math.sin(t * 0.55 + p.fase) * 1.15;
          y = p.by + Math.cos(t * 0.47 + p.fase) * 0.95;
        }
        el.setAttribute("cx", x.toFixed(2));
        el.setAttribute("cy", y.toFixed(2));
      }
      if (foco) {
        if (focoRef.current) {
          focoRef.current.setAttribute("r", (foco.r * (1 + Math.sin(t * 1.7) * 0.07)).toFixed(2));
        }
        // Dos anillos desfasados medio ciclo: uno sale mientras el otro se apaga.
        for (let i = 0; i < anillos.current.length; i += 1) {
          const el = anillos.current[i];
          if (!el) continue;
          const ciclo = (t * 0.5 + i * 0.5) % 1;
          el.setAttribute("r", (foco.r * (1.15 + ciclo * 1.1)).toFixed(2));
          el.setAttribute("stroke", `rgba(251,146,60,${(0.55 * (1 - ciclo)).toFixed(2)})`);
        }
      }

      // El punto de paso que ya es cuello late; los demas se reponen a su estado
      // fijo en cada cuadro, porque React no reescribe un atributo que no cambio
      // en el JSX y se quedarian con el radio del ultimo cuadro animado.
      for (let i = 0; i < pulsos.current.length; i += 1) {
        const p = pulsos.current[i];
        if (!p) continue;
        if (p.pulsa) {
          const ciclo = (t * 0.6) % 1;
          p.el.setAttribute("r", (p.r * (1 + ciclo * 0.8)).toFixed(2));
          p.el.setAttribute("stroke", `rgba(${p.rgb},${(0.85 * (1 - ciclo)).toFixed(2)})`);
        } else {
          p.el.setAttribute("r", p.r.toFixed(2));
          p.el.setAttribute("stroke", p.trazo);
        }
      }
    },
    [gente, velocidad, foco]
  );

  useEffect(() => {
    if (reducido) {
      pintar(0);
      return undefined;
    }

    let raf = 0;
    let inicio = null;

    const cuadro = (ts) => {
      if (inicio === null) inicio = ts;
      pintar((ts - inicio) / 1000);
      raf = requestAnimationFrame(cuadro);
    };

    // Si la pestaña se va al fondo se corta el loop: la maquina del presentador
    // no tiene por que gastar CPU en un plano que nadie esta viendo.
    const alCambiarVisibilidad = () => {
      cancelAnimationFrame(raf);
      if (!document.hidden) {
        inicio = null;
        raf = requestAnimationFrame(cuadro);
      }
    };

    raf = requestAnimationFrame(cuadro);
    document.addEventListener("visibilitychange", alCambiarVisibilidad);

    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener("visibilitychange", alCambiarVisibilidad);
    };
  }, [pintar, reducido]);

  return (
    <svg
      data-testid="croquis"
      viewBox={`0 0 ${W} ${H}`}
      className="w-full aspect-video block"
      role="img"
      aria-label="Plano esquemático del recinto con la gente simulada"
    >
      <defs>
        <pattern id="croquis-rejilla" width="8" height="8" patternUnits="userSpaceOnUse">
          <path d="M8 0H0V8" fill="none" stroke="rgba(148,163,184,.10)" strokeWidth=".3" />
        </pattern>
        <radialGradient id="croquis-foco">
          <stop offset="0%" stopColor="rgba(255,226,140,.95)" />
          <stop offset="35%" stopColor="rgba(239,68,68,.5)" />
          <stop offset="100%" stopColor="rgba(120,20,10,0)" />
        </radialGradient>
        <linearGradient id="croquis-escala">
          <stop offset="0%" stopColor="#1e293b" />
          <stop offset="45%" stopColor="#b45309" />
          <stop offset="75%" stopColor="#ef4444" />
          <stop offset="100%" stopColor="#fde68a" />
        </linearGradient>
      </defs>

      <rect width={W} height={H} fill="#050d17" />
      <rect width={W} height={H} fill="url(#croquis-rejilla)" />

      {/* Media cancha teñida: es como se marca "zona oriente" cuando el aviso
          no cae sobre una zona con aforo, sino sobre un lado del recinto. */}
      {MITADES.map((m) => {
        const tono = cfg.mitades && cfg.mitades[m.id];
        if (!tono) return null;
        return (
          <rect
            key={m.id}
            data-testid={`croquis-mitad-${m.id}`}
            x={m.x}
            y="12"
            width={m.w}
            height="70"
            fill={`rgba(${TONO_RGB[tono] || TONO_RGB[""]},.12)`}
          />
        );
      })}

      {/* Mitades del recinto: los nombres que usan las filas del incendio. */}
      <line x1="80" y1="12" x2="80" y2="82" stroke="rgba(148,163,184,.18)" strokeWidth=".3" strokeDasharray="2 2" />
      {MITADES.map((m) => (
        <text
          key={m.id}
          x={m.x + m.w / 2}
          y="84.5"
          textAnchor="middle"
          fontSize="2.6"
          letterSpacing=".4"
          fill="rgba(148,163,184,.55)"
          className="font-mono"
        >
          {m.etiqueta}
        </text>
      ))}

      {/* Escenario: referencia para ubicarse, sin estado ni gente. */}
      <g data-testid="croquis-escenario">
        <rect
          x={ESCENARIO.x}
          y={ESCENARIO.y}
          width={ESCENARIO.w}
          height={ESCENARIO.h}
          rx="1.5"
          fill="rgba(30,41,59,.55)"
          stroke="rgba(100,116,139,.45)"
          strokeWidth=".4"
          strokeDasharray="1.6 1.6"
        />
        <text
          x={ESCENARIO.x + ESCENARIO.w / 2}
          y={ESCENARIO.y + ESCENARIO.h / 2 + 1}
          textAnchor="middle"
          fontSize="3"
          fill="rgba(148,163,184,.8)"
          className="font-mono"
        >
          ESCENARIO
        </text>
      </g>

      {/* Rutas: por donde circula la gente. */}
      {Object.keys(RUTAS).map((k) => (
        <polyline
          key={k}
          points={RUTAS[k].map((p) => p.join(",")).join(" ")}
          fill="none"
          stroke={cfg.converge && k === "plaza-sur" ? "rgba(56,189,248,.55)" : "rgba(56,189,248,.18)"}
          strokeWidth={cfg.converge && k === "plaza-sur" ? ".7" : ".4"}
          strokeDasharray="2.5 2"
        />
      ))}

      {/* Flechas de flujo sobre la ruta que se satura. Son la justificacion de la
          prediccion: el punto de paso se marca como posible cuello PORQUE la
          gente va toda hacia alli, no porque ya este saturado. */}
      {cfg.converge &&
        [0.3, 0.55, 0.8].map((t) => {
          const [x, y] = puntoEnRuta("plaza-sur", t);
          return (
            <path
              key={t}
              d="M -2 -2 L 0 0 L -2 2"
              fill="none"
              stroke="rgba(56,189,248,.75)"
              strokeWidth=".55"
              strokeLinecap="round"
              transform={`translate(${x.toFixed(2)} ${y.toFixed(2)}) rotate(${anguloEnRuta("plaza-sur", t).toFixed(1)})`}
            />
          );
        })}

      {/* Zonas.
          En modo aforo (vista de flujos) el color y el porcentaje salen de la
          fila de la tabla: el aforo ES lo que se vigila.
          En los avisos de contingencia el aforo no es el tema, asi que las zonas
          quedan como geografia neutra y lo unico con color es lo que el sistema
          esta señalando. Si no, las dos vistas se ven iguales. */}
      {zonas.map((z) => {
        const rgb = TONO_RGB[z.tono] || TONO_RGB[""];
        return (
          <g key={z.id} data-testid={`croquis-zona-${z.testid}`}>
            <rect
              x={z.x}
              y={z.y}
              width={z.w}
              height={z.h}
              rx="1.5"
              fill={modoAforo ? `rgba(${rgb},${(0.07 + z.ocupacion * 0.2).toFixed(3)})` : "rgba(148,163,184,.05)"}
              stroke={modoAforo ? `rgba(${rgb},.6)` : "rgba(100,116,139,.45)"}
              strokeWidth={modoAforo ? ".5" : ".4"}
            />
            <text
              x={z.x + 2.5}
              y={z.y + 5.5}
              fontSize="3.1"
              fill={modoAforo ? "#e2e8f0" : "rgba(148,163,184,.75)"}
              className="font-mono"
            >
              {z.id.toUpperCase()}
            </text>
            {modoAforo && (
              <text
                x={z.x + z.w - 2.5}
                y={z.y + 5.5}
                textAnchor="end"
                fontSize="3.1"
                fill={`rgba(${rgb},1)`}
                className="font-mono"
              >
                {Math.round(z.ocupacion * 100)}%
              </text>
            )}
          </g>
        );
      })}

      {/* Clave termica: la escala de calor y los focos que el sistema YA conoce.
          Estan aqui para que el paso 2 signifique algo — lo que dispara el aviso
          es un foco NUEVO, no cualquier cosa caliente del recinto. */}
      {termico && (
        <g data-testid="croquis-escala">
          <text x="8" y="70" fontSize="2.5" fill="rgba(148,163,184,.7)" className="font-mono">
            ESCALA DE CALOR
          </text>
          <rect x="8" y="72" width="30" height="2.6" rx="1.3" fill="url(#croquis-escala)" />
          <text x="8" y="78.5" fontSize="2.2" fill="rgba(148,163,184,.55)" className="font-mono">
            AMBIENTE
          </text>
          <text x="38" y="78.5" textAnchor="end" fontSize="2.2" fill="rgba(148,163,184,.55)" className="font-mono">
            FUEGO
          </text>
        </g>
      )}

      {termico &&
        (cfg.focosConocidos || []).map((f) => (
          <g key={f.etiqueta} data-testid="croquis-foco-conocido">
            <circle cx={f.x} cy={f.y} r={f.r} fill="rgba(251,146,60,.28)" />
            <circle
              cx={f.x}
              cy={f.y}
              r={f.r}
              fill="none"
              stroke="rgba(251,146,60,.5)"
              strokeWidth=".3"
              strokeDasharray="1 1"
            />
            <text
              x={f.x}
              y={f.y + f.r + 3.4}
              textAnchor="middle"
              fontSize="2.4"
              fill="rgba(148,163,184,.8)"
              className="font-mono"
            >
              {f.etiqueta}
            </text>
          </g>
        ))}

      {/* Foco de calor: crece por paso, respira, y suelta dos anillos que se
          expanden. Los anillos son lo que hace que se lea como un aviso y no
          como una mancha mas del plano. */}
      {foco && (
        <g data-testid="croquis-foco">
          <circle ref={focoRef} cx={foco.x} cy={foco.y} r={foco.r} fill="url(#croquis-foco)" />
          {[0, 1].map((i) => (
            <circle
              key={i}
              ref={(el) => {
                anillos.current[i] = el;
              }}
              cx={foco.x}
              cy={foco.y}
              r={foco.r * 1.3}
              fill="none"
              stroke="rgba(251,146,60,.5)"
              strokeWidth=".4"
            />
          ))}
        </g>
      )}

      {/* La gente. */}
      <g data-testid="croquis-gente">
        {gente.map((p, i) => {
          const [x, y] = posicionInicial(p);
          return (
            <circle
              key={i}
              ref={(el) => {
                nodos.current[i] = el;
              }}
              cx={x}
              cy={y}
              r={p.tipo === "ruta" ? ".95" : ".85"}
              fill={p.tipo === "ruta" ? "#e2e8f0" : "#94a3b8"}
              opacity={p.tipo === "ruta" ? ".95" : ".85"}
            />
          );
        })}
      </g>

      {/* Puntos de paso del recinto. Dos muros con un hueco, orientados al flujo,
          y su estado escrito al lado. En el paso donde ya hay cuello se marca
          TAMBIEN el siguiente punto rio arriba: la fila se propaga hacia atras,
          y ver los dos juntos es lo que le sirve al operador. */}
      {(cfg.cuellos || []).map((c, i) => {
        const e = ESTILO_CUELLO[c.estado] || ESTILO_CUELLO.fluye;
        return (
          <g key={c.id} data-testid={`croquis-cuello-${c.id}`}>
            <g transform={`rotate(${c.ang || 0} ${c.x} ${c.y})`}>
              <line
                x1={c.x}
                y1={c.y + 3}
                x2={c.x}
                y2={c.y + 8.5}
                stroke={`rgba(${e.rgb},${e.alfa})`}
                strokeWidth={e.grosor}
                strokeLinecap="round"
              />
              <line
                x1={c.x}
                y1={c.y - 3}
                x2={c.x}
                y2={c.y - 8.5}
                stroke={`rgba(${e.rgb},${e.alfa})`}
                strokeWidth={e.grosor}
                strokeLinecap="round"
              />
            </g>
            <circle
              ref={(el) => {
                pulsos.current[i] = el
                  ? {
                      el,
                      r: 4.6,
                      pulsa: c.estado === "cuello",
                      rgb: e.rgb,
                      trazo: `rgba(${e.rgb},${e.alfa * 0.7})`,
                    }
                  : null;
              }}
              cx={c.x}
              cy={c.y}
              r="4.6"
              fill="none"
              stroke={`rgba(${e.rgb},${e.alfa * 0.7})`}
              strokeWidth=".4"
              strokeDasharray={e.dash}
            />
            {/* La etiqueta va abajo salvo que el punto este pegado al borde
                inferior del plano (`arriba`), donde no cabria. */}
            <text
              x={c.x}
              y={c.y + (c.arriba ? -9 : 13.5)}
              textAnchor="middle"
              fontSize="2.5"
              fill={`rgba(${e.rgb},${c.estado === "fluye" ? 0.6 : 1})`}
              className="font-mono"
            >
              {e.etiqueta}
            </text>
          </g>
        );
      })}

      {/* El corro de curiosos: sin marcarlo, la gente que se junta alrededor se
          confundia con el resto del publico y no se leia como parte del aviso. */}
      {cfg.cumulo && rina && rina.cerca && (
        <g data-testid="croquis-corro">
          <circle
            cx={cfg.cumulo.x}
            cy={cfg.cumulo.y}
            r={cfg.cumulo.r + 2}
            fill="none"
            stroke="rgba(245,158,11,.45)"
            strokeWidth=".35"
            strokeDasharray="1.8 1.6"
          />
          <text
            x={cfg.cumulo.x}
            y={cfg.cumulo.y + cfg.cumulo.r + 5.6}
            textAnchor="middle"
            fontSize="2.4"
            fill="rgba(245,158,11,.9)"
            className="font-mono"
          >
            {rina.cerca.toUpperCase()}
          </text>
        </g>
      )}

      {/* Las dos personas que el sistema marca junto en la riña, con la distancia
          entre ellas escrita: es la medicion que dispara el aviso, asi que se ve
          en el plano y no solo en la tabla. */}
      {cfg.destacados && (
        <g data-testid="croquis-destacados">
          {cfg.destacados.length === 2 && (
            <line
              x1={cfg.destacados[0].x}
              y1={cfg.destacados[0].y}
              x2={cfg.destacados[1].x}
              y2={cfg.destacados[1].y}
              stroke="rgba(245,158,11,.5)"
              strokeWidth=".35"
              strokeDasharray="1.2 1.2"
            />
          )}
          {rina && rina.distancia && (
            <text
              x={rina.mx}
              y={rina.my - 4.2}
              textAnchor="middle"
              fontSize="2.6"
              fill="rgba(251,191,36,.95)"
              className="font-mono"
            >
              {rina.distancia.toUpperCase()}
            </text>
          )}
          {cfg.destacados.map((d, i) => (
            <g key={i}>
              <circle cx={d.x} cy={d.y} r="1.5" fill="#fbbf24" />
              <circle cx={d.x} cy={d.y} r="2.8" fill="none" stroke="rgba(251,191,36,.6)" strokeWidth=".35" />
            </g>
          ))}
        </g>
      )}
    </svg>
  );
}

// Convierte coordenadas del plano a porcentaje del contenedor: los overlays de
// <Escena> van en %, y asi caen exactamente donde esta la cosa en el plano en
// vez de con numeros ajustados a ojo.
export function enPorcentaje({ x, y, w, h }) {
  return {
    x: Number(((x / W) * 100).toFixed(1)),
    y: Number(((y / H) * 100).toFixed(1)),
    w: Number(((w / W) * 100).toFixed(1)),
    h: Number(((h / H) * 100).toFixed(1)),
  };
}

// Rectangulo de cada zona, listo para pasarselo a un overlay.
export const ZONA_PCT = ZONAS.reduce((acc, z) => {
  acc[z.id] = enPorcentaje(z);
  return acc;
}, {});
